import { useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { uniqueId } from "lodash";
import * as d3 from "d3";
import { lineChartClassNames, LineChartProps } from "@evergis/charts";

import { formatAttributeValue } from "../../../utils/formatAttributeValue";
import { getReferenceLayerInfos } from "../../../ducks/showFeature/selectors";
import { ConfigDataSource, ConfigRelatedDataSource } from "../types";

const HEIGHT_OFFSET = 20;
const FILL_OPACITY = 0.28;

const lineGenerator = d3
  .line()
  .x(d => d[0])
  .y(d => d[1])
  .curve(d3.curveMonotoneX);

const areaGenerator = (height: number) =>
  d3
    .area()
    .defined(d => d !== null)
    .x(d => d[0])
    .y0(height - HEIGHT_OFFSET)
    .y1(d => d[1])
    .curve(d3.curveMonotoneX);

function getLinePoints(numPoints: number, svgLine: SVGPathElement | null): [number, number][] | [] {
  if (!svgLine?.getTotalLength?.()) {
    return [];
  }

  const lineLength = svgLine.getTotalLength();

  let interval: number;

  if (numPoints === 1) {
    interval = 0;
  } else {
    interval = lineLength / (numPoints - 1);
  }

  return d3.range(numPoints).map(d => {
    const value = d * interval;
    const { x, y } = svgLine.getPointAtLength(value);

    return [x, y];
  });
}

function wrap() {
  const width = 80;
  const padding = 10;
  const self = d3.select(this);
  let textLength = self.node().getComputedTextLength();
  let text = self.text();

  while (textLength > width - 2 * padding && text.length > 0) {
    text = text.slice(0, -1);
    self.text(`${text}...`);
    textLength = self.node().getComputedTextLength();
  }
}

// https://jr.everpoint.ru/browse/PUB-5221
/* function wrap2(text, width, content) {
  text.each(() => {
    const textElem = d3.select(this);
    const words = content.split(/\s+/).reverse();
    let word;
    let line = [];
    let lineNumber = 0;
    const lineHeight = 1.1; // ems
    const x = 0; // textElem.attr("x");
    const y = 0; // textElem.attr("y");
    const dy = 0; // parseFloat(text.attr("dy")),
    // eslint-disable-next-line newline-per-chained-call
    let tspan = textElem.text(null).append("tspan").attr("x", x).attr("y", y).attr("dy", `${dy}em`);

    // eslint-disable-next-line no-cond-assign
    while ((word = words.pop())) {
      line.push(word);
      tspan.text(line.join(" "));
      if (tspan.node().getComputedTextLength() > width) {
        line.pop();
        tspan.text(line.join(" "));
        line = [word];
        tspan = textElem
          .append("tspan")
          .attr("x", x)
          .attr("y", y)
          // eslint-disable-next-line no-plusplus
          .attr("dy", `${++lineNumber * lineHeight + dy}em`)
          .text(word);
      }
    }
  });
}*/

export const useChartChange = ({
  dataSources,
  chartId,
  width,
  height,
  relatedAttributes,
  defaultColor,
  fontColor,
}: {
  dataSources: ConfigDataSource[];
  chartId: string;
  width: string | number;
  height: number;
  relatedAttributes: ConfigRelatedDataSource[];
  defaultColor: string;
  fontColor: string;
}): [LineChartProps["customize"], (range: [number, number]) => void] => {
  const layerInfos = useSelector(getReferenceLayerInfos);

  const strokeColors = relatedAttributes.filter(({ chartAxis }) => chartAxis === "y").map(({ axisColor }) => axisColor);

  const ref = useRef<{
    path: d3.Selection<d3.BaseType, unknown, SVGPathElement, unknown> | null;
    area: d3.Selection<d3.BaseType, unknown, SVGPathElement, unknown> | null;
    points: [number, number][] | [];
  }>({
    path: null,
    area: null,
    points: [],
  });

  const onChange = useCallback(
    (range?: [number, number]) => {
      const { path, area, points } = ref.current;
      let filteredPoints = [...points];

      if (range) {
        const hundred = 100;
        const x1 = +width * (range[0] / hundred);
        const x2 = +width * (range[1] / hundred);

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        filteredPoints = points.filter(([x]: unknown) => x >= x1 && x <= x2);
      }

      path && path.attr("d", lineGenerator(filteredPoints));
      area && area.attr("d", areaGenerator(height)(filteredPoints));
    },
    [height, width],
  );

  const customize = useCallback(
    ({ svg }) => {
      svg.style("overflow", "visible");
      svg
        .selectAll(
          `.${lineChartClassNames.lineChartXScaleGlobal} line,
              .${lineChartClassNames.lineChartYScaleGlobal} line,
              .domain`,
        )
        .each((_, index, nodes) => {
          nodes[index].remove();
        });
      svg.selectAll(`.${lineChartClassNames.lineChartXScaleGlobal} .tick`).each((_, index, nodes) => {
        if (!index) {
          nodes[index].style.textAnchor = "start";
        }
        if (index === nodes.length - 1) {
          nodes[index].style.textAnchor = "end";
        }
        if (index && index < nodes.length - 1) {
          nodes[index].remove();
        }
      });
      // svg.selectAll(`.${lineChartClassNames.lineChartXScaleGlobal} .tick text`).call(wrap2, 5, "!!!");
      svg.selectAll(`.${lineChartClassNames.lineChartXScaleGlobal} .tick text`).attr("y", 16);
      svg
        .selectAll(`.${lineChartClassNames.lineChartYScaleGlobal} .tick text`)
        .text(item => {
          if (!item) return 0;

          const dataSource = dataSources?.find(({ name }) => name === relatedAttributes[0].dataSourceName);
          const layerInfo = layerInfos.find(({ name }) => name === dataSource.layerName);
          const attribute = layerInfo?.layerDefinition.attributes[relatedAttributes[0].attributeName];

          return attribute ? formatAttributeValue(attribute.type, item, attribute.stringFormat, true) : item;
        })
        .each(wrap);
      svg.selectAll(".tick text").attr("style", `color: ${fontColor}`);

      const global = svg.select(`.${lineChartClassNames.lineChartLinesGlobal}`);
      const lineChartLines = svg.selectAll(`.${lineChartClassNames.lineChartLine}`);
      const defs: string[] = [];

      lineChartLines.each((_, index, nodes) => {
        const lineChartLine = nodes[index];
        const gradientId = uniqueId(`${chartId}-gradient-`);
        const color = strokeColors[index] || defaultColor;

        const newPath = global
          .append("path")
          .attr("stroke", color)
          .attr("stroke-width", "0")
          .attr("class", lineChartClassNames.lineChartLine);

        const points: [number, number][] | [] = getLinePoints(+width, lineChartLine);

        const area = global
          .append("path")
          .attr("fill", `url(#${gradientId})`)
          .attr("stroke-width", "0")
          .attr("fill-opacity", FILL_OPACITY);

        defs.push(
          `
          <linearGradient id="${gradientId}" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stop-color="${color}" stop-opacity="1" />
            <stop offset="1" stop-color="${color}" stop-opacity="0" />
          </linearGradient>
        `,
        );

        ref.current = {
          path: newPath,
          area,
          points,
        };

        onChange();
      });

      svg.append("defs").html(() => defs.join(""));
    },
    [fontColor, dataSources, layerInfos, relatedAttributes, chartId, strokeColors, defaultColor, width, onChange],
  );

  return [customize, onChange];
};
